/**
 * Sviatkový kalendár a working-day posun pre schedule engine, vrstva 2.
 * Čistý, deterministický, zero I/O. Všetka dátumová matematika beží na
 * "YYYY-MM-DD" reťazcoch cez civil.js — žiadne epoch ms, žiadny Intl.
 */

import { isoWeekday, addCivilDays } from './civil.js';

/**
 * Veľkonočná nedeľa cez Gaussov computus (gregoriánsky kalendár).
 * Vracia [year, month, day] (month 1-based).
 */
function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return [year, month, day];
}

/**
 * "YYYY-MM-DD" z komponentov (month 1-based).
 */
function toYyyyMmDd(year, month, day) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * SK zákonné dni pracovného pokoja pre daný rok: pevné dátumy + Veľkonočné
 * (Veľký piatok + Veľkonočný pondelok). Deterministické, pole "YYYY-MM-DD".
 */
export function skHolidayDates(year) {
  const dates = [];

  // Pevné dátumy
  dates.push(toYyyyMmDd(year, 1, 1));   // Deň vzniku SR / Nový rok
  dates.push(toYyyyMmDd(year, 1, 6));   // Zjavenie Pána
  dates.push(toYyyyMmDd(year, 5, 1));   // Sviatok práce
  dates.push(toYyyyMmDd(year, 5, 8));   // Deň víťazstva nad fašizmom
  dates.push(toYyyyMmDd(year, 7, 5));   // Sv. Cyril a Metod
  dates.push(toYyyyMmDd(year, 8, 29));  // Výročie SNP
  dates.push(toYyyyMmDd(year, 9, 15));  // Sedembolestná Panna Mária
  dates.push(toYyyyMmDd(year, 11, 1));  // Sviatok všetkých svätých
  dates.push(toYyyyMmDd(year, 11, 17)); // Deň boja za slobodu a demokraciu
  dates.push(toYyyyMmDd(year, 12, 24)); // Štedrý deň
  dates.push(toYyyyMmDd(year, 12, 25)); // Prvý sviatok vianočný
  dates.push(toYyyyMmDd(year, 12, 26)); // Druhý sviatok vianočný

  // Veľkonočné (pohyblivé) — počítané cez civil.js na reťazci
  const [y, m, d] = easterSunday(year);
  const easter = toYyyyMmDd(y, m, d);
  dates.push(addCivilDays(easter, -2)); // Veľký piatok
  dates.push(addCivilDays(easter, 1));  // Veľkonočný pondelok

  // Deduplikuj + zoraď (deterministicky)
  return [...new Set(dates)].sort();
}

/**
 * Sviatkový kalendár z poľa "YYYY-MM-DD" + meta { version }.
 * Nepriehľadná množina: .has(date) + .version. Jediný zdroj sviatkov, aký engine vidí.
 */
export function createHolidayCalendar(dates, meta) {
  const set = new Set(dates);
  return {
    has(date) {
      return set.has(date);
    },
    version: meta?.version ?? null,
  };
}

/**
 * Pracovný deň = nie víkend a nie v kalendári.
 * ISO deň: 1=Po .. 6=So, 7=Ne.
 */
export function isWorkingDay(date, calendar) {
  const wd = isoWeekday(date);
  if (wd === 6 || wd === 7) return false; // So/Ne
  if (calendar && calendar.has(date)) return false;
  return true;
}

/**
 * Posun na najbližší pracovný deň podľa módu, deň po dni v civilnom priestore.
 * mode: 'none' | 'previous_working' | 'next_working'
 */
export function adjustToWorkingDay(date, mode, calendar) {
  if (mode === 'none') return date;
  if (mode !== 'previous_working' && mode !== 'next_working') {
    throw new Error(`Invalid mode: ${mode}`);
  }

  const direction = mode === 'previous_working' ? -1 : 1;
  let current = date;
  while (!isWorkingDay(current, calendar)) {
    current = addCivilDays(current, direction);
  }
  return current;
}
